import React from 'react';
import { Activity } from 'lucide-react';
import { DeploymentRequest } from '../../types';
import { computePunchCardData } from '../../utils/deploymentAnalytics';

interface DeploymentPunchCardProps {
  deployments: DeploymentRequest[];
}

const HOUR_TICKS = [0, 4, 8, 12, 16, 20];

// Sequential blue ramp (validated palette) — magnitude encoded by both size
// and step, not hue alone, so it still reads for a low-vision or CVD viewer.
const BLUE_RAMP = ['#cde2fb', '#9ec5f4', '#6da7ec', '#3987e5', '#256abf', '#184f95'];

const DeploymentPunchCard: React.FC<DeploymentPunchCardProps> = ({ deployments }) => {
  const cells = computePunchCardData(deployments);
  const maxCount = Math.max(1, ...cells.map((c) => c.count));

  const colorFor = (count: number) => {
    if (count === 0) return '#e1e0d9';
    const step = Math.min(BLUE_RAMP.length - 1, Math.floor((count / maxCount) * (BLUE_RAMP.length - 1)));
    return BLUE_RAMP[step];
  };

  const radiusFor = (count: number) => {
    if (count === 0) return 2;
    return 3 + (count / maxCount) * 9;
  };

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-blue-100 text-blue-600">
            <Activity className="w-4 h-4" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Deployment Punch Card</h3>
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-4">When deployment requests are typically made, by day and hour</p>

      <div className="overflow-x-auto">
        <div className="min-w-[560px]">
          {/* Hour axis */}
          <div className="flex ml-10 mb-1">
            {Array.from({ length: 24 }, (_, h) => (
              <div key={h} className="flex-1 text-center text-[10px] text-gray-400">
                {HOUR_TICKS.includes(h) ? `${h}:00` : ''}
              </div>
            ))}
          </div>

          {dayLabels.map((label, day) => (
            <div key={label} className="flex items-center mb-1">
              <div className="w-10 text-xs text-gray-500 text-right pr-2 shrink-0">{label}</div>
              <div className="flex flex-1">
                {Array.from({ length: 24 }, (_, hour) => {
                  const cell = cells.find((c) => c.day === day && c.hour === hour)!;
                  return (
                    <div key={hour} className="flex-1 flex items-center justify-center h-6 border-l border-gray-50 first:border-l-0">
                      <div
                        className="rounded-full"
                        style={{
                          width: radiusFor(cell.count) * 2,
                          height: radiusFor(cell.count) * 2,
                          backgroundColor: colorFor(cell.count),
                        }}
                        title={`${label} ${hour}:00 — ${cell.count} deployment${cell.count === 1 ? '' : 's'}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4 text-xs text-gray-500">
        <span>Fewer</span>
        {BLUE_RAMP.map((c) => (
          <span key={c} className="w-3 h-3 rounded-full" style={{ backgroundColor: c }} />
        ))}
        <span>More</span>
      </div>
    </div>
  );
};

export default DeploymentPunchCard;
